import React, {ChangeEvent, useId, useMemo, useState} from 'react';
import classNames from 'classnames';
import {AssetType, DetectionType} from '@taopix/taopix-core';
import {Checkbox, Shimmer, ThemeName, getTheme} from '@taopix/taopix-design-system';
import {DragAndDropNames} from '../../Enums';
import Drag from '../DragAndDrop/Drag';
import {Tile} from './BackgroundsList';
import {CheckboxProps} from '@taopix/taopix-design-system/dist/types/Components/Checkbox/Checkbox';
import useImageLoader from '../../Hooks/Assets/useImageLoader';
import {useInView} from 'react-intersection-observer';
import {useTranslation} from 'react-i18next';

export type BackgroundTileProps = {
  background: Tile;
  checked: boolean;
  onClick: (event: React.MouseEvent | React.ChangeEvent, selectedBackground: Tile) => void;
  onKeyDown?: (event: React.KeyboardEvent, selectedBackground: Tile) => void;
  parentSection: string;
} & Pick<CheckboxProps, 'preventManualUncheck'>;

const BackgroundTile = ({
  background,
  checked,
  onClick,
  onKeyDown,
  parentSection,
  preventManualUncheck,
}: BackgroundTileProps) => {
  const {t} = useTranslation(['boxpalettes']);
  const {ref, inView} = useInView({
    /* Optional options */
    threshold: 0.2,
  });
  const src = useImageLoader(background.asset, AssetType.Background, inView);
  const [loaded, setLoaded] = useState(false);
  const baseId = useId().replaceAll(':', '');

  const thumbnailContainerClassess = classNames([
    'relative',
    'before:block',
    'before:pt-[100%]',
    'cursor-pointer',
    'mt-[var(--gap)]',
    'ml-[var(--gap)]',
    // 'overflow-hidden',
    'w-[calc((100%-(var(--gap)*var(--columns)))/var(--columns))]',
    getTheme(ThemeName.Section, true, !checked, false),
    'rounded-[min(theme(borderRadius.themeCornerSize),5px)]',
  ]);

  const thumbnailClassess = classNames('object-cover w-full h-full');

  const id = useMemo(() => {
    return `${baseId}-${background.assetRef}-${parentSection}-isColour`.replace('--', '-');
  }, []);

  if (background.isColour) {
    return (
      <li
        key={`background-${background.assetRef}`}
        className={thumbnailContainerClassess}
        data-theme={ThemeName.Section} // Allow theme tips to work on this element
      >
        <div
          className={'absolute top-0 left-0 cursor-pointer w-full h-full'}
          data-test-id={`${parentSection}-${background.assetRef}`}
        >
          <Checkbox
            label={t('boxpalettes:str_LabelBackgroundColour')}
            id={id}
            defaultChecked={checked}
            appearance={'border'}
            hideLabel
            onChange={(isChecked: boolean, event: ChangeEvent) => {
              onClick(event, background);
            }}
            onKeyDown={event => {
              onKeyDown?.(event, background);
            }}
            className={classNames(
              'w-full h-full',
              checked && 'border-solid border-[5px] border-white' // Separates the accent border from the colour to help see it
            )}
            preventManualUncheck={preventManualUncheck}
          >
            <Drag
              name={DragAndDropNames.PAGE}
              key={`background-${background.assetRef}-${parentSection}`}
              detectionType={DetectionType.Background}
              item={background}
              allowDrag={parentSection === 'backgrounds'}
            >
              <div
                className={thumbnailClassess}
                style={{backgroundColor: `#${background.src}`}}
              ></div>
            </Drag>
          </Checkbox>
        </div>
      </li>
    );
  } else {
    return (
      <li
        key={`background-${background.assetRef}`}
        className={thumbnailContainerClassess}
        data-theme={ThemeName.Section} // Allow theme tips to work on this element
        ref={ref}
      >
        {!loaded && <Shimmer className={'absolute top-0'} />}
        {inView && (
          <div className={'absolute top-0 left-0 cursor-pointer w-full h-full'}>
            <Checkbox
              label={t('*:str_LabelBackground')}
              id={`background-checkbox-${background.assetRef}`}
              defaultChecked={checked}
              appearance={'border'}
              hideLabel
              onChange={(isChecked: boolean, event: ChangeEvent) => {
                if (loaded) {
                  onClick(event, background);
                }
              }}
              onKeyDown={event => {
                onKeyDown?.(event, background);
              }}
              className={'w-full h-full'}
              preventManualUncheck={preventManualUncheck}
            >
              <Drag
                name={DragAndDropNames.PAGE}
                key={`background-${background.assetRef}`}
                detectionType={DetectionType.Background}
                item={background}
              >
                {src !== '' ? (
                  <img
                    className={thumbnailClassess}
                    draggable={true}
                    src={src}
                    alt={'background'}
                    onError={e => {
                      (e.target as HTMLImageElement).classList.add('hidden');
                      // console.error(e);
                    }}
                    onLoad={e => {
                      if ((e.target as HTMLImageElement).classList.contains('hidden')) {
                        (e.target as HTMLImageElement).classList.remove('hidden');
                      }
                      setLoaded(true);
                    }}
                  />
                ) : (
                  <></>
                )}
              </Drag>
            </Checkbox>
          </div>
        )}
      </li>
    );
  }
};
export default BackgroundTile;
